import { useEffect, useMemo, useState } from "react";
import { DateTime } from "luxon";
import { Check, ChevronsUpDown } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Spinner } from "@/components/ui/spinner";
import { DatePicker } from "@/components/ui/date-picker";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import { appTimeZone } from "@/lib/timezone";
import { createAdminTimeOff, updateAdminTimeOff } from "@/lib/api/admin-time-off";
import { publishCalendarRefresh } from "@/lib/realtime/appointment-realtime-bridge";

// Per-doctor scope types. Public holidays are imported (cross-tenant), so
// "holiday" isn't offered here — but it IS offered for the global scope below
// so admins can declare custom company-wide closures.
const PER_DOCTOR_TYPE_OPTIONS = [
  { value: "vacation", label: "Vacation" },
  { value: "ooo", label: "Out of office" },
  { value: "block", label: "Block" },
  { value: "personal", label: "Personal" },
];

// Global scope types. Drops the doctor-specific options ("ooo", "personal" —
// nonsensical org-wide) and adds "holiday" for custom company-wide holidays
// the user wants to declare outside the public-holiday import flow.
const GLOBAL_TYPE_OPTIONS = [
  { value: "holiday", label: "Holiday" },
  { value: "vacation", label: "Vacation" },
  { value: "block", label: "Block" },
];

function typeOptionsForScope(scope) {
  return scope === "global" ? GLOBAL_TYPE_OPTIONS : PER_DOCTOR_TYPE_OPTIONS;
}

// Default type when the form opens for a given scope.
function defaultTypeForScope(scope) {
  return scope === "global" ? "holiday" : "vacation";
}

// Timezone to prefill for a NEW record. Honors the app-wide global timezone override when set (so
// newly-created records match what the rest of the app shows), else the machine zone as before.
function browserTimezone() {
  return appTimeZone();
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function dateToYmd(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function ymdToDate(ymd) {
  if (typeof ymd !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

function combineDateAndTimeInZone(ymd, hhmm, timezone) {
  const tz = String(timezone || "").trim() || "UTC";
  const dt = DateTime.fromISO(`${ymd}T${hhmm}`, { zone: tz });
  return dt.isValid ? dt : null;
}

function selectedDateToYmd(selectedDate) {
  if (!selectedDate) return dateToYmd(new Date());
  if (typeof selectedDate.toISODate === "function") return selectedDate.toISODate();
  if (selectedDate instanceof Date) return dateToYmd(selectedDate);
  return dateToYmd(new Date());
}

const DEFAULT_FORM = {
  scope: "doctor",
  type: "vacation",
  ownerId: "",
  tenantId: "",
  title: "",
  description: "",
  allDay: true,
  startDate: "",
  endDate: "",
  startTime: "09:00",
  endTime: "17:00",
  timezone: "UTC",
};

function buildInitialForm({ mode, entry, selectedDate, doctorOptions, tenantOptions, defaultScope }) {
  if (mode === "edit" && entry) {
    const tz = entry.timezone || browserTimezone();
    const startLocal = entry.startAt?.setZone ? entry.startAt.setZone(tz) : entry.startAt;
    const endLocal = entry.endAt?.setZone ? entry.endAt.setZone(tz) : entry.endAt;
    const allDay = Boolean(entry.allDay);
    return {
      scope: entry.scope === "global" ? "global" : "doctor",
      type: entry.type || "vacation",
      ownerId: entry.ownerId || "",
      tenantId: entry.tenantId || "",
      title: entry.title || "",
      description: entry.description || "",
      allDay,
      startDate: startLocal?.toFormat?.("yyyy-LL-dd") || "",
      endDate: allDay
        ? endLocal?.minus?.({ days: 1 }).toFormat?.("yyyy-LL-dd") || endLocal?.toFormat?.("yyyy-LL-dd") || ""
        : endLocal?.toFormat?.("yyyy-LL-dd") || "",
      startTime: startLocal?.toFormat?.("HH:mm") || "09:00",
      endTime: endLocal?.toFormat?.("HH:mm") || "17:00",
      timezone: tz,
    };
  }
  const ymd = selectedDateToYmd(selectedDate);
  const scope = defaultScope === "global" ? "global" : "doctor";
  return {
    ...DEFAULT_FORM,
    scope,
    type: defaultTypeForScope(scope),
    // Pre-fill the doctor picker when there's exactly one option (no choice
    // to make). For global scope we deliberately default `tenantId` to "" —
    // that means "all locations", which is the most powerful org-wide intent.
    ownerId:
      scope === "doctor" && doctorOptions && doctorOptions.length === 1
        ? doctorOptions[0].id
        : "",
    tenantId: "",
    startDate: ymd,
    endDate: ymd,
    timezone: browserTimezone(),
  };
}

function ComboBox({ value, onChange, options, placeholder, disabled, emptyText, label, idKey = "id", labelKey = "name" }) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((opt) => opt[idKey] === value) || null,
    [options, value, idKey],
  );
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-label={label}
          disabled={disabled}
          className={cn(
            "h-9 w-full justify-between font-normal",
            !selected && "text-muted-foreground",
          )}
        >
          {selected ? selected[labelKey] : placeholder}
          <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0" align="start">
        <Command>
          <CommandInput placeholder={placeholder} />
          <CommandList>
            <CommandEmpty>{emptyText || "No matches."}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option[idKey]}
                  value={option[labelKey]}
                  onSelect={() => {
                    onChange(option[idKey]);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 size-4",
                      option[idKey] === value ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {option[labelKey]}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function TimeOffFormSheet({
  open,
  onOpenChange,
  mode = "create",
  entry,
  selectedDate,
  doctorOptions = [],
  tenantOptions = [],
  defaultScope,
  onSaved,
}) {
  const [form, setForm] = useState(() =>
    buildInitialForm({ mode, entry, selectedDate, doctorOptions, tenantOptions, defaultScope }),
  );
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(buildInitialForm({ mode, entry, selectedDate, doctorOptions, tenantOptions, defaultScope }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, entry?.id, defaultScope]);

  const updateField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  // Switching scope mid-flow (create only): clear the no-longer-relevant id
  // and reset `type` if the current value isn't valid for the new scope.
  const setScope = (nextScope) => {
    if (mode === "edit") return;
    setForm((prev) => {
      const validTypes = typeOptionsForScope(nextScope).map((opt) => opt.value);
      const nextType = validTypes.includes(prev.type) ? prev.type : defaultTypeForScope(nextScope);
      return {
        ...prev,
        scope: nextScope,
        type: nextType,
        ownerId: nextScope === "doctor" ? prev.ownerId : "",
        tenantId: nextScope === "global" ? prev.tenantId : "",
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    if (form.scope === "doctor" && !form.ownerId) {
      toast.error("Select a doctor.");
      return;
    }
    // For global scope, an empty tenantId means "every location" — no error.
    if (!form.startDate || !form.endDate) {
      toast.error("Start and end date are required.");
      return;
    }

    let startIso;
    let endIso;
    if (form.allDay) {
      const startDt = combineDateAndTimeInZone(form.startDate, "00:00", form.timezone);
      // For inclusive end-date, treat the day as ending at next-day midnight.
      const endYmd = DateTime.fromISO(form.endDate)
        .plus({ days: 1 })
        .toFormat("yyyy-LL-dd");
      const endDt = combineDateAndTimeInZone(endYmd, "00:00", form.timezone);
      if (!startDt || !endDt) {
        toast.error("Invalid date or timezone.");
        return;
      }
      if (endDt <= startDt) {
        toast.error("End date must be on or after the start date.");
        return;
      }
      startIso = startDt.toUTC().toISO();
      endIso = endDt.toUTC().toISO();
    } else {
      const startDt = combineDateAndTimeInZone(form.startDate, form.startTime, form.timezone);
      const endDt = combineDateAndTimeInZone(form.endDate, form.endTime, form.timezone);
      if (!startDt || !endDt) {
        toast.error("Invalid date or time.");
        return;
      }
      if (endDt <= startDt) {
        toast.error("End must be after start.");
        return;
      }
      startIso = startDt.toUTC().toISO();
      endIso = endDt.toUTC().toISO();
    }

    const body = {
      scope: form.scope,
      type: form.type,
      allDay: form.allDay,
      title: form.title.trim(),
      description: form.description.trim() || undefined,
      time: {
        start: startIso,
        end: endIso,
        timezone: form.timezone || browserTimezone(),
      },
    };

    if (form.scope === "doctor") {
      body.ownerId = form.ownerId;
    } else {
      // Global scope: no doctor reference. A specific `tenantId` scopes the
      // block to one location; blank means "every location" (omit the key so
      // the server treats it as cross-tenant, like imported holidays).
      if (form.tenantId) body.tenantId = form.tenantId;
    }

    setSubmitting(true);
    try {
      if (mode === "edit" && entry?.id) {
        await updateAdminTimeOff({ id: entry.id, body });
        toast.success("Time off updated.");
      } else {
        await createAdminTimeOff({ body });
        toast.success("Time off created.");
      }
      publishCalendarRefresh({ source: "time-off-save" });
      if (typeof onSaved === "function") onSaved();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save time off.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="h-full max-h-dvh w-full gap-0 p-0 sm:max-w-md">
        <SheetHeader className="relative z-10 shrink-0 border-b border-border bg-background px-6 pb-4 pl-6 pr-14 pt-5 text-left">
          <SheetTitle>{mode === "edit" ? "Edit time off" : "New time off"}</SheetTitle>
          <SheetDescription>
            Per-doctor entries block one doctor's availability. Global entries close a
            specific location for everyone — or every location, when no location is
            picked. Public holidays are imported via the Import holidays dialog.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-full min-h-0 flex-1">
        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-5 pb-8">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Scope</label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant={form.scope === "doctor" ? "default" : "outline"}
                size="sm"
                onClick={() => setScope("doctor")}
                disabled={mode === "edit"}
                className="h-8"
              >
                Per-doctor
              </Button>
              <Button
                type="button"
                variant={form.scope === "global" ? "default" : "outline"}
                size="sm"
                onClick={() => setScope("global")}
                disabled={mode === "edit"}
                className="h-8"
              >
                Global (company-wide)
              </Button>
            </div>
            {mode === "edit" ? (
              <p className="text-[11px] text-muted-foreground/70">
                Scope can't change after creation.
              </p>
            ) : null}
          </div>

          {form.scope === "doctor" ? (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Doctor</label>
              <ComboBox
                value={form.ownerId}
                onChange={(v) => updateField("ownerId", v)}
                options={doctorOptions}
                placeholder="Select doctor…"
                label="Doctor"
                emptyText="No doctors available."
              />
            </div>
          ) : (
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Location</label>
              {/* "All locations" is a real first-class option (id: "") so the
                  picker renders it as the active selection — not just an empty
                  state. Pick a specific location to scope the block instead. */}
              <ComboBox
                value={form.tenantId}
                onChange={(v) => updateField("tenantId", v)}
                options={[
                  { id: "", name: "All locations" },
                  ...tenantOptions,
                ]}
                placeholder="All locations"
                label="Location"
                emptyText="No locations available."
              />
              <p className="text-[11px] text-muted-foreground/70">
                {form.tenantId
                  ? "Applies to every doctor at this location."
                  : "Applies to every doctor at every location."}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Type</label>
            <div className="flex flex-wrap gap-2">
              {typeOptionsForScope(form.scope).map((opt) => (
                <Button
                  key={opt.value}
                  type="button"
                  variant={form.type === opt.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => updateField("type", opt.value)}
                  className="h-8"
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Title</label>
            <Input
              value={form.title}
              onChange={(e) => updateField("title", e.target.value)}
              placeholder="e.g. Vacation, Christmas Day, Conference"
              maxLength={120}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Description (optional)</label>
            <Input
              value={form.description}
              onChange={(e) => updateField("description", e.target.value)}
              placeholder="Notes for staff…"
              maxLength={300}
            />
          </div>

          <Separator />

          <div className="flex items-center gap-2">
            <input
              id="time-off-all-day"
              type="checkbox"
              checked={form.allDay}
              onChange={(e) => updateField("allDay", e.target.checked)}
              className="size-4"
            />
            <label htmlFor="time-off-all-day" className="text-sm">
              All day
            </label>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">Start date</label>
              <DatePicker
                date={ymdToDate(form.startDate)}
                setDate={(d) => updateField("startDate", dateToYmd(d))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-muted-foreground">End date</label>
              <DatePicker
                date={ymdToDate(form.endDate)}
                setDate={(d) => updateField("endDate", dateToYmd(d))}
              />
            </div>
          </div>

          {!form.allDay ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">Start time</label>
                <Input
                  type="time"
                  value={form.startTime}
                  onChange={(e) => updateField("startTime", e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-muted-foreground">End time</label>
                <Input
                  type="time"
                  value={form.endTime}
                  onChange={(e) => updateField("endTime", e.target.value)}
                />
              </div>
            </div>
          ) : null}

          <div className="space-y-1.5">
            <label className="text-xs font-medium text-muted-foreground">Timezone</label>
            <Input
              value={form.timezone}
              onChange={(e) => updateField("timezone", e.target.value)}
              placeholder="e.g. Europe/Warsaw"
            />
          </div>

        </form>
        </ScrollArea>

        <div className="shrink-0 border-t border-border bg-background px-6 py-4">
          <div className="flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleSubmit} disabled={submitting} aria-busy={submitting}>
              {submitting ? <Spinner className="mr-2 size-4" /> : null}
              {mode === "edit" ? "Save changes" : "Create"}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
